import type { GeospatialFile } from '@lib/GeospatialFile'
import type { PlanFileReadError } from 'api-validations/crusher'
import { PlanAsset } from 'api-validations/locutus'
import { type Cff, cffKey } from 'shared-ui/operation-wide-data'
import { isNonNil } from 'utils'

import { toInspectRequest } from './inspectRequest'
import {
  type CffTarget,
  CffTargetStatus,
  hasRates,
  type ParsedPlanEntry,
  type PlanFileReading,
  type PlanningField,
  resolveCffTarget,
  UNASSIGNED_CFF,
  type UploadAssetType
} from './planEntry'

/**
 * Why a source could not be read. A file holding no plans reads as empty instead.
 */
export type PlanReadProblem = Exclude<PlanFileReadError, PlanFileReadError.NoAssets>

export enum ReadStatus {
  /**
   * Files are missing, so nothing has been sent to be read.
   */
  Unreadable = 'unreadable',
  Reading = 'reading',
  /**
   * Being read again under a new reading, still showing the last entries.
   */
  Rereading = 'rereading',
  Ready = 'ready',
  Failed = 'failed'
}

type SourceRead =
  | { status: ReadStatus.Unreadable }
  | { status: ReadStatus.Reading }
  | { status: ReadStatus.Rereading; entries: readonly ParsedPlanEntry[]; unreadable: number }
  | {
      status: ReadStatus.Ready
      entries: readonly ParsedPlanEntry[]
      unreadable: number
      /**
       * Why the last re-read failed.
       */
      rereadProblem?: PlanReadProblem
    }
  | { status: ReadStatus.Failed; problem: PlanReadProblem }

/**
 * The entries a source is showing. During a re-read, the last set of entries
 * the source read.
 */
export const shownEntries = (read: SourceRead): readonly ParsedPlanEntry[] | undefined =>
  read.status === ReadStatus.Ready || read.status === ReadStatus.Rereading
    ? read.entries
    : undefined

/**
 * A new source's status: reading, or waiting on missing parts.
 */
export const initialRead = (source: Pick<PlanUploadSource, 'file' | 'reading'>): SourceRead =>
  toInspectRequest(source).readable
    ? { status: ReadStatus.Reading }
    : { status: ReadStatus.Unreadable }

/**
 * One group of picked files and what crusher made of it. The id is new with
 * every file-pick, so a stale read finds nothing to update.
 */
export type PlanUploadSource = {
  id: string
  file: GeospatialFile
  reading: PlanFileReading
  read: SourceRead
}

/**
 * The grower's edits to a row.
 */
type EntryEdits = {
  name?: string
  target?: CffTarget
  excluded?: boolean
}

export enum CreateStatus {
  Waiting = 'waiting',
  Creating = 'creating',
  Created = 'created',
  Failed = 'failed'
}

export enum CreateFailure {
  /**
   * The field already has an asset of this type with this name.
   */
  DuplicateName = 'duplicateName',
  /**
   * A new field could not be registered.
   */
  UnknownField = 'unknownField',
  TooLarge = 'tooLarge',
  /**
   * Any other refusal, with the service's message.
   */
  Rejected = 'rejected'
}

export type EntryOutcome =
  | { status: CreateStatus.Waiting }
  | { status: CreateStatus.Creating }
  | { status: CreateStatus.Created; createdId: string }
  | { status: CreateStatus.Failed; failure: CreateFailure.DuplicateName }
  | { status: CreateStatus.Failed; failure: CreateFailure.UnknownField }
  | { status: CreateStatus.Failed; failure: CreateFailure.TooLarge }
  | { status: CreateStatus.Failed; failure: CreateFailure.Rejected; message: string }

export enum SubmitStatus {
  Idle = 'idle',
  Running = 'running',
  Settled = 'settled'
}

type Outcomes = Readonly<Record<string, EntryOutcome | undefined>>

type Submission =
  | { status: SubmitStatus.Idle }
  | { status: SubmitStatus.Running; outcomes: Outcomes }
  | { status: SubmitStatus.Settled; outcomes: Outcomes }

export type PlanUploadState = {
  sources: readonly PlanUploadSource[]
  edits: Readonly<Record<string, EntryEdits | undefined>>
  submission: Submission
}

export const EMPTY_PLAN_UPLOAD: PlanUploadState = {
  sources: [],
  edits: {},
  submission: { status: SubmitStatus.Idle }
}

export enum PlanUploadActionType {
  /**
   * Replaces the review with a fresh pick.
   */
  Picked = 'picked',
  /**
   * Supplies a missing shapefile part.
   */
  PartSupplied = 'partSupplied',
  SourceDropped = 'sourceDropped',
  SourceRead = 'sourceRead',
  SourceFailed = 'sourceFailed',
  /**
   * Reads a source again under a new reading.
   */
  Rereading = 'rereading',
  EntriesReread = 'entriesReread',
  RereadFailed = 'rereadFailed',
  Renamed = 'renamed',
  Targeted = 'targeted',
  Excluded = 'excluded',
  /**
   * Excludes or includes every row at once.
   */
  AllExcluded = 'allExcluded',
  CreateStarted = 'createStarted',
  CreateProgressed = 'createProgressed',
  CreateSettled = 'createSettled',
  Cleared = 'cleared'
}

export type PlanUploadAction =
  | { type: PlanUploadActionType.Picked; sources: readonly PlanUploadSource[] }
  | {
      type: PlanUploadActionType.PartSupplied
      sourceId: string
      file: GeospatialFile
      reading: PlanFileReading
    }
  | { type: PlanUploadActionType.SourceDropped; sourceId: string }
  | {
      type: PlanUploadActionType.SourceRead
      sourceId: string
      entries: readonly ParsedPlanEntry[]
      unreadable: number
    }
  | { type: PlanUploadActionType.SourceFailed; sourceId: string; problem: PlanReadProblem }
  | { type: PlanUploadActionType.Rereading; sourceId: string }
  | {
      type: PlanUploadActionType.EntriesReread
      sourceId: string
      replacements: readonly RereadReplacement[]
    }
  | { type: PlanUploadActionType.RereadFailed; sourceId: string; problem: PlanReadProblem }
  | { type: PlanUploadActionType.Renamed; entryId: string; name: string }
  | { type: PlanUploadActionType.Targeted; entryId: string; target: CffTarget }
  | { type: PlanUploadActionType.Excluded; entryId: string; excluded: boolean }
  | { type: PlanUploadActionType.AllExcluded; excluded: boolean }
  | { type: PlanUploadActionType.CreateStarted; entryIds: readonly string[] }
  | { type: PlanUploadActionType.CreateProgressed; entryId: string; outcome: EntryOutcome }
  | { type: PlanUploadActionType.CreateSettled }
  | { type: PlanUploadActionType.Cleared }

export const assetIdFor = ({ sourceId, index }: { sourceId: string; index: number }) =>
  `${sourceId}#${index}`

export const entryId = ({ assetId, rateColumn }: { assetId: string; rateColumn?: string }) =>
  rateColumn === undefined ? assetId : `${assetId}/${rateColumn}`

const rateColumnOf = (entry: ParsedPlanEntry) =>
  entry.assetType === PlanAsset.Prescription ? entry.rateColumn : undefined

const assetsNamed = (entries: readonly ParsedPlanEntry[], name: string) => [
  ...new Set(entries.filter(({ sourceName }) => sourceName === name).map(({ assetId }) => assetId))
]

export type RereadReplacement = {
  replaces: readonly string[]
  entries: readonly ParsedPlanEntry[]
}

/**
 * Pairs each wanted entry with its counterpart in a re-read, keeping the old id so edits stand.
 * Assets match by name, then by position among assets with the same name. When every wanted row of
 * an asset finds its rate column in the re-read, those rows are replaced one for one. Otherwise the
 * re-read changed how the asset splits, so all of the asset's rows are replaced together.
 */
export const rereadReplacements = ({
  shown,
  reread,
  entryIds
}: {
  shown: readonly ParsedPlanEntry[]
  reread: readonly ParsedPlanEntry[]
  entryIds: readonly string[]
}): readonly RereadReplacement[] => {
  const wanted = shown.filter(({ id }) => entryIds.includes(id))

  return [...new Set(wanted.map(({ assetId }) => assetId))].flatMap((assetId) => {
    const rows = shown.filter((entry) => entry.assetId === assetId)
    const { sourceName } = rows[0]
    const rank = assetsNamed(shown, sourceName).indexOf(assetId)
    const counterpartAssetId = assetsNamed(reread, sourceName).at(rank)
    const counterparts = reread
      .filter((entry) => entry.assetId === counterpartAssetId)
      .map((entry) => ({
        ...entry,
        assetId,
        id: entryId({ assetId, rateColumn: rateColumnOf(entry) })
      }))

    const wantedRows = wanted.filter((entry) => entry.assetId === assetId)
    const oneForOne = wantedRows.flatMap((previous) => {
      const counterpart = counterparts.find(({ id }) => id === previous.id)
      return counterpart === undefined ? [] : [{ replaces: [previous.id], entries: [counterpart] }]
    })

    return oneForOne.length === wantedRows.length
      ? oneForOne
      : [{ replaces: rows.map(({ id }) => id), entries: counterparts }]
  })
}

const editEntry = (
  state: PlanUploadState,
  id: string,
  change: (edits: EntryEdits) => EntryEdits
): PlanUploadState => ({
  ...state,
  edits: { ...state.edits, [id]: change(state.edits[id] ?? {}) }
})

const mapSource = (
  state: PlanUploadState,
  sourceId: string,
  change: (source: PlanUploadSource) => PlanUploadSource
): PlanUploadState => ({
  ...state,
  sources: state.sources.map((source) => (source.id === sourceId ? change(source) : source))
})

const outcomesOf = (submission: Submission): Outcomes =>
  submission.status === SubmitStatus.Idle ? {} : submission.outcomes

export type PlanUploadEntry = { source: PlanUploadSource; entry: ParsedPlanEntry }

/**
 * Every entry in the upload, in pick order.
 */
export const allEntries = (state: PlanUploadState): readonly PlanUploadEntry[] =>
  state.sources.flatMap((source) => {
    const entries = shownEntries(source.read)
    return entries === undefined ? [] : entries.map((entry) => ({ source, entry }))
  })

export const planUploadReducer = (
  state: PlanUploadState,
  action: PlanUploadAction
): PlanUploadState => {
  switch (action.type) {
    case PlanUploadActionType.Picked:
      return { ...EMPTY_PLAN_UPLOAD, sources: action.sources }

    case PlanUploadActionType.PartSupplied:
      return mapSource(state, action.sourceId, (source) => {
        const supplied = { ...source, file: action.file, reading: action.reading }
        return { ...supplied, read: initialRead(supplied) }
      })

    case PlanUploadActionType.SourceDropped:
      return {
        ...state,
        sources: state.sources.filter(({ id }) => id !== action.sourceId)
      }

    case PlanUploadActionType.SourceRead:
      return mapSource(state, action.sourceId, (source) => ({
        ...source,
        read: {
          status: ReadStatus.Ready,
          entries: action.entries,
          unreadable: action.unreadable
        }
      }))

    case PlanUploadActionType.EntriesReread: {
      // Only the re-read entries change; an archive's other assets keep their own readings.
      const replacedAt = new Map(action.replacements.map((r) => [r.replaces[0], r.entries]))
      const replaced = new Set(action.replacements.flatMap(({ replaces }) => replaces))

      const inheritedTargets = action.replacements.flatMap(({ replaces, entries }) => {
        const target = state.edits[replaces[0]]?.target
        if (target === undefined) return []
        return entries
          .filter(({ id }) => state.edits[id] === undefined)
          .map(({ id }) => [id, { target }] as const)
      })

      const reread = mapSource(state, action.sourceId, (source) => {
        if (source.read.status !== ReadStatus.Rereading) return source

        return {
          ...source,
          read: {
            status: ReadStatus.Ready,
            unreadable: source.read.unreadable,
            entries: source.read.entries.flatMap(
              (entry) => replacedAt.get(entry.id) ?? (replaced.has(entry.id) ? [] : [entry])
            )
          }
        }
      })

      return { ...reread, edits: { ...reread.edits, ...Object.fromEntries(inheritedTargets) } }
    }

    case PlanUploadActionType.RereadFailed:
      return mapSource(state, action.sourceId, (source) => {
        if (source.read.status !== ReadStatus.Rereading) return source

        return {
          ...source,
          read: {
            status: ReadStatus.Ready,
            unreadable: source.read.unreadable,
            entries: source.read.entries,
            rereadProblem: action.problem
          }
        }
      })

    case PlanUploadActionType.SourceFailed:
      return mapSource(state, action.sourceId, (source) => ({
        ...source,
        read: { status: ReadStatus.Failed, problem: action.problem }
      }))

    case PlanUploadActionType.Rereading:
      // Keep the rows on screen while re-reading, so a change edits them in place.
      return mapSource(state, action.sourceId, (source) => {
        if (source.read.status !== ReadStatus.Ready) return source

        const { entries, unreadable } = source.read
        return { ...source, read: { status: ReadStatus.Rereading, entries, unreadable } }
      })

    case PlanUploadActionType.Renamed:
      return editEntry(state, action.entryId, (edits) => ({ ...edits, name: action.name }))

    case PlanUploadActionType.Targeted:
      return editEntry(state, action.entryId, (edits) => ({ ...edits, target: action.target }))

    case PlanUploadActionType.Excluded:
      return editEntry(state, action.entryId, (edits) => ({ ...edits, excluded: action.excluded }))

    case PlanUploadActionType.AllExcluded:
      return {
        ...state,
        edits: {
          ...state.edits,
          ...Object.fromEntries(
            allEntries(state).map(({ entry }) => [
              entry.id,
              { ...state.edits[entry.id], excluded: action.excluded }
            ])
          )
        }
      }

    case PlanUploadActionType.CreateStarted:
      return {
        ...state,
        submission: {
          status: SubmitStatus.Running,
          outcomes: Object.fromEntries(
            action.entryIds.map((id) => [id, { status: CreateStatus.Waiting }])
          )
        }
      }

    case PlanUploadActionType.CreateProgressed:
      return {
        ...state,
        submission: {
          status: SubmitStatus.Running,
          outcomes: { ...outcomesOf(state.submission), [action.entryId]: action.outcome }
        }
      }

    case PlanUploadActionType.CreateSettled:
      return {
        ...state,
        submission: { status: SubmitStatus.Settled, outcomes: outcomesOf(state.submission) }
      }

    case PlanUploadActionType.Cleared:
      return EMPTY_PLAN_UPLOAD
  }
}

/**
 * What an active read is doing. Values are translation keys.
 */
export enum ReadActivity {
  Idle = 'idle',
  /**
   * Picked sources not yet shown.
   */
  Reading = 'reading',
  /**
   * Displayed rows being re-read with an updated interpretation.
   */
  Updating = 'updating'
}

export const readActivity = (state: PlanUploadState): ReadActivity => {
  const reading = state.sources.some(({ read }) => read.status === ReadStatus.Reading)
  if (reading) return ReadActivity.Reading

  const updating = state.sources.some(({ read }) => read.status === ReadStatus.Rereading)
  return updating ? ReadActivity.Updating : ReadActivity.Idle
}

const isReading = (state: PlanUploadState) => readActivity(state) !== ReadActivity.Idle

/**
 * A review row: what the upload found, with the grower's edits and any create outcome.
 */
export type PlanUploadRow = {
  entry: ParsedPlanEntry
  source: PlanUploadSource
  name: string
  target: CffTarget
  assetType: UploadAssetType
  /**
   * Whether the boundary marks an excluded area.
   */
  inverted: boolean
  excluded: boolean
  outcome?: EntryOutcome
}

export enum DestinationKind {
  Operation = 'operation',
  Field = 'field'
}

export type UploadDestination =
  | { kind: DestinationKind.Operation; fields: readonly PlanningField[] }
  | { kind: DestinationKind.Field; field: PlanningField | undefined }

/**
 * Re-resolves the grower's chosen tuple each time, since a field created during
 * the review turns a new target into an existing one.
 */
const toTarget = ({
  edits,
  entry,
  destination
}: {
  edits: EntryEdits
  entry: ParsedPlanEntry
  destination: UploadDestination
}): CffTarget => {
  if (destination.kind === DestinationKind.Field) {
    const { field } = destination
    if (field === undefined) return UNASSIGNED_CFF
    return { status: CffTargetStatus.Existing, fieldId: field.id, cff: field }
  }

  const { fields } = destination
  const chosen = edits.target
  if (chosen === undefined) return resolveCffTarget({ cff: entry.cff, fields })
  if (chosen.status === CffTargetStatus.Unassigned) return chosen
  return resolveCffTarget({ cff: chosen.cff, fields })
}

const defaultName = (entry: ParsedPlanEntry) => {
  const rateColumn = rateColumnOf(entry)
  return rateColumn === undefined ? entry.sourceName : `${entry.sourceName} ${rateColumn}`
}

export const toRows = ({
  state,
  destination
}: {
  state: PlanUploadState
  destination: UploadDestination
}): readonly PlanUploadRow[] => {
  const outcomes = outcomesOf(state.submission)

  return allEntries(state).map(({ source, entry }) => {
    const edits = state.edits[entry.id] ?? {}

    return {
      entry,
      source,
      name: edits.name ?? defaultName(entry),
      target: toTarget({ edits, entry, destination }),
      assetType: entry.assetType,
      inverted: entry.assetType === PlanAsset.Boundary && entry.inverted,
      excluded: edits.excluded ?? false,
      outcome: outcomes[entry.id]
    }
  })
}

export enum SubmitBlock {
  StillReading = 'stillReading',
  NothingIncluded = 'nothingIncluded',
  Unplaced = 'unplaced',
  Unnamed = 'unnamed',
  Rateless = 'rateless'
}

/**
 * A ready-to-create row with a destination field declared.
 */
export type CreatableRow = PlanUploadRow & {
  target: Extract<CffTarget, { status: CffTargetStatus.Existing | CffTargetStatus.New }>
}

export type CreatePlan =
  | { ready: false; block: SubmitBlock }
  | {
      ready: true
      rows: readonly CreatableRow[]
      /**
       * New fields to register before creating.
       */
      newFields: readonly Cff[]
    }

/**
 * Whether the review can be imported yet, and if so what to create. New fields
 * are deduplicated.
 */
export const toCreatePlan = ({
  state,
  rows
}: {
  state: PlanUploadState
  rows: readonly PlanUploadRow[]
}): CreatePlan => {
  if (isReading(state)) return { ready: false, block: SubmitBlock.StillReading }

  const included = rows.filter(({ excluded }) => !excluded)
  if (included.length === 0) return { ready: false, block: SubmitBlock.NothingIncluded }
  if (included.some(({ name }) => name.trim() === ''))
    return { ready: false, block: SubmitBlock.Unnamed }
  if (included.some(({ entry }) => !hasRates(entry)))
    return { ready: false, block: SubmitBlock.Rateless }

  const creatable = included.filter(
    (row): row is CreatableRow => row.target.status !== CffTargetStatus.Unassigned
  )
  if (creatable.length !== included.length) return { ready: false, block: SubmitBlock.Unplaced }

  const newFields = [
    ...new Map(
      creatable
        .map(({ target }) => (target.status === CffTargetStatus.New ? target.cff : undefined))
        .filter(isNonNil)
        .map((cff) => [cffKey(cff), cff])
    ).values()
  ]

  return { ready: true, rows: creatable, newFields }
}

/**
 * The counts the import band shows. A finished import reports outcomes.
 */
export type ImportSummary = {
  found: number
  sources: number
  included: number
  /**
   * New fields the import will register.
   */
  newFields: number
} & ({ settled: false } | { settled: true; imported: number; failed: number })

export const toImportSummary = ({
  state,
  rows,
  createPlan
}: {
  state: PlanUploadState
  rows: readonly PlanUploadRow[]
  createPlan: CreatePlan
}): ImportSummary => {
  const counts = {
    found: rows.length,
    sources: state.sources.length,
    included: rows.filter(({ excluded }) => !excluded).length,
    newFields: createPlan.ready ? createPlan.newFields.length : 0
  }

  if (state.submission.status !== SubmitStatus.Settled) return { ...counts, settled: false }

  const outcomes = Object.values(state.submission.outcomes).filter(isNonNil)

  return {
    ...counts,
    settled: true,
    imported: outcomes.filter(({ status }) => status === CreateStatus.Created).length,
    failed: outcomes.filter(({ status }) => status === CreateStatus.Failed).length
  }
}
